import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react';
import {useTranslation} from 'react-i18next';
import {isMobile} from 'react-device-detect';
import {Camera, CameraOff, ImageIcon, RotateCcw, Upload} from 'lucide-react';
import {
  Button,
  CaptureCamera,
  DeviceCamera,
  ErrorMessage,
  cn,
  useCameraPermission,
  WebcamErrorType,
  type CaptureVariant,
  type WebcamErrorDetails,
  type VideoConstraints,
  type WebcamRefTypes,
} from '@chekinapp/ui';
import {
  IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB,
  IV_PHOTO_SOURCE,
  STATUSES,
} from '@chekin/core';
import {useErrorModal, useImageFileUpload} from '../../hooks';
import {compressPhoto} from '../../utils/iv/compressPhoto';
import {isTouchDevice} from '../../utils/isTouchDevice';
import {reportCameraError} from '../../utils/reportCameraError';
import {safeCaptureException} from '../../utils/sentry';
import {useAutoCapture} from './useAutoCapture';
import {resolveAutoCapture} from './autoCaptureOverride';
import {CameraDeniedPanel} from './CameraDeniedPanel';
import {PhotoUpload} from './PhotoUpload';
import {useCaptureModeStore} from './useCaptureModeStore';
import {CaptureProcessFrame, type CaptureChecking} from './CaptureProcessFrame';
import {CaptureDock} from './CaptureDock';
import {FaceGuide} from './FaceGuide';
import {RevealCaptureCamera} from './RevealCaptureCamera';

const MANUAL_SHUTTER_FALLBACK_MS = 8000;

const PLACEHOLDER_LINGER_MS = 1000;
const FRAME_GUIDE_LINGER_MS = 2500;

const WEBCAM_QUALITY = {
  minScreenshotW: 800,
  minScreenshotH: 600,
  screenshotMaxSize: IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB,
  imageSmoothing: false,
} as const;

export type CameraCaptureControls = {
  showIntro: () => boolean;
};

export type CameraCaptureProps = {
  variant?: CaptureVariant;
  isUpload?: boolean;
  allowUpload?: boolean;
  allowDeviceCamera?: boolean;
  hints?: ReactNode;
  confirmLabel?: string;
  shutterLabel?: ReactNode;
  autoCapture?: boolean;
  placeholderImage?: string;
  frameGuide?: ReactNode;
  additionalConstraints?: VideoConstraints;
  statusLabel?: ReactNode;
  onCapture: (payload: {
    image: string;
    source: IV_PHOTO_SOURCE;
  }) => void | Promise<string | void>;
  processState?: STATUSES;
  scanningLabel?: ReactNode;
  successLabel?: ReactNode;
  errorTitle?: ReactNode;
  errorDescription?: ReactNode;
  onRetry?: () => void;
  onVerifyLater?: () => void;
  onAutoCaptureUnavailable?: () => void;
  onBusyChange?: (isBusy: boolean) => void;
  onIntroVisibilityChange?: (isVisible: boolean) => void;
  hasKnownCause?: boolean;
  errorHelp?: ReactNode;
  intro?: ReactNode;
  introLabel?: ReactNode;
  uploadStyle?: 'link' | 'button';
  checking?: CaptureChecking;
  faceGuideSize?: 'default' | 'max';
  faceSilhouette?: ReactNode;
  pendingStatusLabel?: ReactNode;
  viewfinderHint?: ReactNode;
  controlRef?: Ref<CameraCaptureControls>;
  canResend?: boolean;
  className?: string;
};

export function CameraCapture({
  variant = 'document',
  isUpload,
  allowUpload = true,
  allowDeviceCamera = true,
  hints,
  confirmLabel,
  shutterLabel,
  autoCapture = false,
  placeholderImage,
  frameGuide,
  additionalConstraints,
  statusLabel,
  onCapture,
  processState = STATUSES.idle,
  scanningLabel,
  successLabel,
  errorTitle,
  errorDescription,
  onRetry,
  onVerifyLater,
  onAutoCaptureUnavailable,
  onBusyChange,
  onIntroVisibilityChange,
  hasKnownCause,
  errorHelp,
  intro,
  introLabel,
  uploadStyle = 'link',
  checking,
  faceGuideSize = 'default',
  faceSilhouette,
  pendingStatusLabel,
  viewfinderHint,
  controlRef,
  canResend,
  className,
}: CameraCaptureProps) {
  const {t} = useTranslation('identity-verification');
  const webcamRef = useRef<WebcamRefTypes>(null);
  const {displayError, ErrorModal} = useErrorModal();
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [isCapturedFromFile, setIsCapturedFromFile] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const isResendingRef = useRef(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [lastSent, setLastSent] = useState<{
    image: string;
    source: IV_PHOTO_SOURCE;
  } | null>(null);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [isPlaceholderVisible, setIsPlaceholderVisible] = useState(true);
  const [hasAutoCaptureStalled, setHasAutoCaptureStalled] = useState(false);
  const [isIntroDismissed, setIsIntroDismissed] = useState(false);
  const isCameraReadyRef = useRef(false);
  const isFace = variant === 'face';
  const CameraView =
    isFace && faceGuideSize === 'max' ? RevealCaptureCamera : CaptureCamera;
  const isAutoCaptureEnabled = useMemo(
    () => resolveAutoCapture(autoCapture) && !isUpload,
    [autoCapture, isUpload],
  );
  const {isDenied: isPermissionDenied} = useCameraPermission({enabled: !isUpload});
  // Safari has no Permissions API for the camera, so a denial only ever
  // arrives as a getUserMedia error. Remember it, or those browsers would
  // fall through to the generic error modal and never see the panel that
  // tells the guest how to switch the camera back on.
  const [isDeniedByError, setIsDeniedByError] = useState(false);
  const isCameraDenied = isPermissionDenied || isDeniedByError;

  /**
   * A denied camera is not a failure to report: CameraDeniedPanel already
   * says what happened and how to undo it, so a generic modal on top of it
   * only buries those steps. Everything else still gets the modal, with
   * the reason the camera itself gave.
   */
  const handleCameraError = useCallback(
    (error: WebcamErrorDetails) => {
      reportCameraError(error, isFace ? 'selfie' : 'document');
      if (error.type === WebcamErrorType.Permission) {
        setIsDeniedByError(true);
        return;
      }
      displayError({}, {title: error.title, message: error.message});
    },
    [displayError, isFace],
  );

  const isSuccess = processState === STATUSES.success;
  const isError = processState === STATUSES.error;
  const isScanning = isCapturing || processState === STATUSES.loading;
  const isBusy = isScanning || isSuccess || isError;

  const isFrameBusy = isBusy && !uploadedImage;
  useEffect(() => {
    onBusyChange?.(isFrameBusy);
  }, [isFrameBusy, onBusyChange]);

  const isIntroVisible = Boolean(intro) && !isIntroDismissed;
  useImperativeHandle(
    controlRef,
    () => ({
      showIntro: () => {
        if (!intro || !isIntroDismissed) return false;
        setIsIntroDismissed(false);
        return true;
      },
    }),
    [intro, isIntroDismissed],
  );
  useEffect(() => {
    onIntroVisibilityChange?.(isIntroVisible);
  }, [isIntroVisible, onIntroVisibilityChange]);

  useEffect(() => {
    if (processState === STATUSES.loading) setIsCapturing(false);
    if (processState === STATUSES.idle) {
      setIsCapturing(false);
      setCapturedImage(null);
      isCameraReadyRef.current = false;
      setIsCameraReady(false);
      setIsPlaceholderVisible(true);
    }
  }, [processState]);

  useEffect(() => {
    if (isIntroVisible) setIsPlaceholderVisible(true);
  }, [isIntroVisible]);

  useEffect(() => {
    if ((!placeholderImage && !frameGuide) || !isPlaceholderVisible || isIntroVisible) {
      return;
    }
    const timeout = setTimeout(
      () => setIsPlaceholderVisible(false),
      frameGuide ? FRAME_GUIDE_LINGER_MS : PLACEHOLDER_LINGER_MS,
    );
    return () => clearTimeout(timeout);
  }, [placeholderImage, frameGuide, isPlaceholderVisible, isIntroVisible]);

  const {
    open: openFileDialog,
    error: uploadError,
    reset: resetUpload,
    inputRef,
    inputProps,
  } = useImageFileUpload({onImage: setUploadedImage});

  useEffect(() => {
    if (isUpload) openFileDialog();
  }, [isUpload, openFileDialog]);

  const sendCapture = useCallback(
    async (image: string, source: IV_PHOTO_SOURCE) => {
      try {
        const processedImage = await onCapture({image, source});
        if (processedImage) {
          setCapturedImage(processedImage);
        }
      } catch (err) {
        safeCaptureException(err, 'Processing the IV photo');
        displayError(err);
      }
    },
    [onCapture, displayError],
  );

  const applyCapture = useCallback(
    async (image: string, source: IV_PHOTO_SOURCE) => {
      setIsCapturedFromFile(source === IV_PHOTO_SOURCE.external);
      setCapturedImage(image);
      setIsCapturing(true);

      let finalImage: string;
      try {
        finalImage = await compressPhoto(image);
      } catch (err) {
        safeCaptureException(err, 'Compressing the IV photo');
        setIsCapturing(false);
        setCapturedImage(null);
        displayError(err);
        return;
      }

      setCapturedImage(finalImage);
      setLastSent({image: finalImage, source});
      await sendCapture(finalImage, source);
    },
    [sendCapture, displayError],
  );

  const handleResend = useCallback(() => {
    if (!lastSent || isResendingRef.current) return;
    isResendingRef.current = true;
    setIsCapturing(true);
    void sendCapture(lastSent.image, lastSent.source).finally(() => {
      isResendingRef.current = false;
    });
  }, [lastSent, sendCapture]);

  const handleTakePhoto = useCallback(async () => {
    try {
      if (!isCameraReadyRef.current) return;
      const image = await webcamRef.current?.takePhoto();

      if (image) {
        await applyCapture(image, IV_PHOTO_SOURCE.appCamera);
      }
    } catch (err) {
      safeCaptureException(err, 'Taking the IV photo');
      displayError(err);
    }
  }, [applyCapture, displayError]);

  const {
    isActive: isAutoActive,
    faceGuideState,
    hint: autoHint,
    cameraStatus: captureCameraStatus,
    handleManualShutter,
    start: startAutoCapture,
  } = useAutoCapture({
    enabled: isAutoCaptureEnabled,
    isFace,
    videoRef: webcamRef,
    processState,
    onCapture: handleTakePhoto,
    onUnavailable: onAutoCaptureUnavailable,
  });

  useEffect(() => {
    if (!isAutoCaptureEnabled || !isCameraReady) return;

    const timeout = setTimeout(
      () => setHasAutoCaptureStalled(true),
      MANUAL_SHUTTER_FALLBACK_MS,
    );
    return () => clearTimeout(timeout);
  }, [isAutoCaptureEnabled, isCameraReady]);

  const handleCameraReady = useCallback(() => {
    isCameraReadyRef.current = true;
    setIsCameraReady(true);
    startAutoCapture();
  }, [startAutoCapture]);

  const faceOverlay = isFace ? (
    <>
      <FaceGuide
        state={isAutoActive ? faceGuideState : 'searching'}
        size={faceGuideSize}
        silhouette={faceSilhouette}
        live={isCameraReady}
      />
      {viewfinderHint && (
        <p className="pointer-events-none absolute inset-x-3 bottom-2.5 z-[3] m-0 text-center text-[14px] font-medium leading-5 text-white/80">
          {viewfinderHint}
        </p>
      )}
    </>
  ) : undefined;

  const handleDeviceCapture = useCallback(
    (photo?: Promise<string>) => {
      if (!photo) return;
      photo
        .then(image => applyCapture(image, IV_PHOTO_SOURCE.nativeCamera))
        .catch(err => {
          safeCaptureException(err, 'Taking the IV photo with the device camera');
          displayError(err);
        });
    },
    [applyCapture, displayError],
  );

  const deviceCameraFallback =
    isMobile && isTouchDevice() && allowDeviceCamera ? (
      <DeviceCamera
        facingMode={isFace ? 'user' : 'environment'}
        onChange={handleDeviceCapture}
        className="w-full max-w-[340px]"
      />
    ) : null;

  const hiddenFileInput = <input ref={inputRef} {...inputProps} />;

  const setCaptureMode = useCaptureModeStore(state => state.setMode);
  useEffect(() => {
    if (uploadedImage) setCaptureMode('crop');
    else if (isIntroVisible) setCaptureMode('intro');
    else setCaptureMode(isCameraDenied ? 'denied' : 'camera');
    return () => setCaptureMode('camera');
  }, [uploadedImage, isIntroVisible, isCameraDenied, setCaptureMode]);

  const handlePickAnotherFile = useCallback(() => {
    resetUpload();
    openFileDialog();
  }, [openFileDialog, resetUpload]);

  if (uploadedImage) {
    return (
      <div
        className={cn('iv-camera-capture flex flex-col items-center gap-5', className)}
      >
        <PhotoUpload
          image={uploadedImage}
          onConfirm={image => {
            setUploadedImage(null);
            applyCapture(image, IV_PHOTO_SOURCE.external);
          }}
          hints={hints}
          confirmLabel={confirmLabel}
          onChangePhoto={handlePickAnotherFile}
          onUseCamera={
            isUpload
              ? undefined
              : () => {
                  setUploadedImage(null);
                  resetUpload();
                }
          }
        />
        {hiddenFileInput}
      </div>
    );
  }

  if (isBusy) {
    const isResendable = isError && canResend && Boolean(lastSent);
    const pickFileAsRetry = isCapturedFromFile && !isResendable && !onRetry;
    const canPickDifferentFile = isCapturedFromFile && !pickFileAsRetry;

    return (
      <>
        <CaptureProcessFrame
          variant={variant}
          checking={checking}
          state={
            isScanning ? STATUSES.loading : isSuccess ? STATUSES.success : STATUSES.error
          }
          image={capturedImage}
          scanningLabel={scanningLabel}
          successLabel={successLabel}
          errorTitle={errorTitle}
          errorDescription={errorDescription}
          onRetry={
            isResendable ? handleResend : pickFileAsRetry ? openFileDialog : onRetry
          }
          retryLabel={
            pickFileAsRetry
              ? t('choose_a_different_file')
              : isResendable
              ? t('try_again')
              : isCapturedFromFile
              ? t('retake_photo')
              : undefined
          }
          retryIcon={
            pickFileAsRetry ? (
              <Upload className="h-[18px] w-[18px] flex-none" />
            ) : undefined
          }
          onRetake={isResendable ? onRetry : undefined}
          onUploadFile={
            canPickDifferentFile || (allowUpload && !isCapturedFromFile)
              ? openFileDialog
              : undefined
          }
          uploadLabel={canPickDifferentFile ? t('choose_a_different_file') : undefined}
          isFromFile={isCapturedFromFile}
          hasKnownCause={hasKnownCause}
          isServiceFailure={canResend}
          errorHelp={errorHelp}
          onVerifyLater={onVerifyLater}
          className={className}
        />
        {hiddenFileInput}
      </>
    );
  }

  if (isUpload) {
    return (
      <div
        className={cn('iv-camera-capture flex flex-col items-center gap-4', className)}
      >
        <CaptureDock>
          <div className="iv-camera-capture__actions flex w-full flex-col items-center gap-3">
            <Button onClick={openFileDialog} className="w-full max-w-[340px]">
              {t('upload_file')}
            </Button>
            {uploadError && <ErrorMessage>{uploadError}</ErrorMessage>}
          </div>
        </CaptureDock>
        {hiddenFileInput}
      </div>
    );
  }

  const placeholderNode = frameGuide ? (
    <div
      className={cn(
        'h-full w-full transition-opacity duration-500',
        isPlaceholderVisible ? 'opacity-100' : 'opacity-0',
      )}
    >
      {frameGuide}
    </div>
  ) : placeholderImage ? (
    <img
      src={placeholderImage}
      alt=""
      className={cn(
        'max-h-full max-w-full object-contain transition-opacity duration-500',
        isPlaceholderVisible ? 'opacity-50' : 'opacity-0',
      )}
    />
  ) : null;

  const uploadButton = !allowUpload ? null : (
    <>
      <Button
        variant="outline"
        onClick={openFileDialog}
        className="w-full max-w-[340px] lg:w-[280px]"
      >
        <ImageIcon />
        {t('choose_from_gallery')}
      </Button>
      {uploadError && <ErrorMessage>{uploadError}</ErrorMessage>}
    </>
  );

  const uploadLink = !allowUpload ? null : (
    <div className="flex flex-col items-center gap-1.5">
      <p className="m-0 flex items-center justify-center gap-1.5 text-[13px] font-medium text-[var(--chekin-color-gray-1)]">
        <CameraOff width={18} height={18} />
        {t('no_camera_question')}
        <button
          type="button"
          onClick={openFileDialog}
          className="font-semibold text-[var(--chekin-color-brand-blue)] hover:opacity-80"
        >
          {t('upload_a_file_instead')}
        </button>
      </p>
      {uploadError && <ErrorMessage>{uploadError}</ErrorMessage>}
    </div>
  );

  const uploadAlt = uploadStyle === 'button' ? uploadButton : uploadLink;

  if (isCameraDenied && !isIntroVisible) {
    return (
      <div
        className={cn('iv-camera-capture flex flex-col items-center gap-5', className)}
      >
        <CameraDeniedPanel />

        <CaptureDock>
          <div className="iv-camera-capture__actions flex w-full flex-col items-center gap-3">
            <Button
              onClick={() => {
                setIsDeniedByError(false);
                void navigator.mediaDevices
                  ?.getUserMedia({video: true})
                  .catch(() => setIsDeniedByError(true));
              }}
              className="w-full max-w-[340px] gap-2"
            >
              <RotateCcw className="h-[18px] w-[18px]" />
              {t('try_again')}
            </Button>
            {deviceCameraFallback}
            {uploadAlt}
          </div>
        </CaptureDock>
        {hiddenFileInput}
      </div>
    );
  }

  const cameraStatusLabel = isAutoActive
    ? autoHint
    : !isCameraReady && pendingStatusLabel
    ? pendingStatusLabel
    : statusLabel ?? t('place_document_inside_frame');

  return (
    <div className={cn('iv-camera-capture flex flex-col items-center gap-6', className)}>
      {isIntroVisible ? (
        intro
      ) : (
        <CameraView
          ref={webcamRef}
          variant={variant}
          status={isAutoActive ? captureCameraStatus : undefined}
          showScanLine={!isFace && isAutoActive && !frameGuide}
          overlay={faceOverlay}
          additionalConstraints={additionalConstraints}
          statusLabel={cameraStatusLabel}
          placeholder={placeholderNode}
          handlePermissionDenied={false}
          onReady={handleCameraReady}
          onError={handleCameraError}
          {...WEBCAM_QUALITY}
        />
      )}

      {hints}

      <CaptureDock>
        <div className="iv-camera-capture__actions flex w-full flex-col items-center gap-3">
          {(isIntroVisible || !isAutoActive || hasAutoCaptureStalled) && (
            <Button
              onClick={
                isIntroVisible ? () => setIsIntroDismissed(true) : handleManualShutter
              }
              disabled={!isIntroVisible && !isCameraReady}
              className="iv-camera-capture__shutter w-full max-w-[340px] lg:w-[280px]"
            >
              {isIntroVisible ? (
                introLabel ?? t('start_scanning')
              ) : (
                <>
                  <Camera className="h-[18px] w-[18px]" />
                  {shutterLabel ?? t('take_a_photo')}
                </>
              )}
            </Button>
          )}
          {!isIntroVisible && deviceCameraFallback}
          {uploadAlt}
        </div>
      </CaptureDock>
      {hiddenFileInput}

      <ErrorModal />
    </div>
  );
}
